/**
 * Purpose: Tiny, framework-free registry of {@link ClientToolDescriptor}s
 *          a host SPA wants the agent to be able to call. The registry
 *          lives inside the widget at runtime; the driver reads from it
 *          on every turn and forwards declarations to the LLM service.
 *
 * Implementation notes:
 *   - P0 ships only the raw `register({ name, description, parameters,
 *     execute })` form. The ergonomic `defineTool({ params: zodSchema })`
 *     wrapper that converts Zod → JSON Schema is a P2 concern and lives
 *     in a future `define-tool.ts` next to this file.
 *   - Registration is **idempotent under replacement**: registering a
 *     tool with the same `name` overwrites the prior one. This is
 *     deliberate so React effects re-running on rerender don't throw —
 *     a host can call `register(...)` from a `useEffect` cleanup or
 *     hot-module-reload path safely.
 *   - The registry is intentionally not generic — it stores tools as
 *     `ClientToolDescriptor` (any args, unknown result). Per-tool
 *     argument typing is the host's responsibility at the call site;
 *     the LLM stops being type-safe the moment its arguments cross the
 *     wire as JSON anyway.
 *
 * Created: 2026-05-08 17:00
 *
 * Changelog:
 *   - 2026-05-08 17:00 — Initial version. P0 stub: empty registry that
 *                        exists so the driver wiring compiles end-to-end.
 *                        Real surface lands in P2.
 */
export class ClientToolRegistry {
    #tools = new Map();
    /** Register (or replace) a tool by name. */
    register(tool) {
        this.#tools.set(tool.name, tool);
    }
    /** Remove a tool by name. No-op if absent. */
    unregister(name) {
        this.#tools.delete(name);
    }
    /** Snapshot of the current tool list, in insertion order. */
    list() {
        return Array.from(this.#tools.values());
    }
    /** Whether the registry has at least one tool. */
    get size() {
        return this.#tools.size;
    }
}
//# sourceMappingURL=client-tool-registry.js.map